import { useState } from "react";
import { Navigate } from "react-router-dom";

import { useAuthStore } from "@/features/auth/store";
import { IconRail } from "@/features/dashboard/components/IconRail";
import { cn, formatDate, formatNok } from "@/lib/utils";
import { useLoanStatus, type LoanStatusLine, type LoanStatusResult } from "./api/loanStatus";

/** Stat cell — identical markup/classes to admin's StatCard (AdminUI.tsx), kept local to
 *  avoid a cross-feature import; teal accent bar + uppercase label + value + hint. */
function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="relative border-white/10 px-3 py-2.5 first:border-l-0 [&:not(:first-child)]:border-l">
      <div className="pl-3 before:absolute before:inset-y-2 before:left-0 before:w-[3px] before:rounded-full before:bg-teal-400/80">
        <div className="text-[10px] font-medium uppercase tracking-wider text-white/40">{label}</div>
        <div className="mt-0.5 text-lg font-semibold leading-tight text-white sm:text-xl">{value}</div>
        {hint && <div className="text-[11px] text-white/45">{hint}</div>}
      </div>
    </div>
  );
}

const TH = "py-1 pr-2 text-left text-[10px] font-medium uppercase tracking-wide text-white/40 whitespace-nowrap";
const TD = "py-1 pr-2 align-top text-[11px] text-white whitespace-nowrap";
// Secondary columns — dropped below the `sm` breakpoint so rows never wrap on mobile.
const HIDE_MOBILE = "hidden sm:table-cell";
// Company/address cells: truncate instead of wrapping (full value on hover via title=).
const TRUNC = "max-w-[84px] truncate sm:max-w-[220px]";

const TABS = [
  { id: "samlet", label: "Samlet" },
  { id: "alle", label: "Alle lån" },
  { id: "renter", label: "Renter" },
  { id: "forlengelse", label: "Forlengelse" },
  { id: "pant", label: "Pant" },
  { id: "betalt", label: "Betalt" },
  { id: "utbetaling", label: "Utbetaling" },
] as const;
type TabId = (typeof TABS)[number]["id"];

function ExtBadge({ rate }: { rate: number | null }) {
  if (rate == null) return null;
  return (
    <span className="inline-flex items-center rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-300 ring-1 ring-inset ring-amber-500/30 whitespace-nowrap">
      {rate.toLocaleString("nb-NO")} %
    </span>
  );
}

function RateCell({ l }: { l: LoanStatusLine }) {
  return l.extended ? <ExtBadge rate={l.extensionRate} /> : <span className="text-white/45">{l.rate} %</span>;
}

/** A Table wrapped in a horizontal-scroll safety net — belt and suspenders alongside the
 *  hidden-on-mobile columns: if a row still doesn't fit, it scrolls instead of breaking. */
function TableScroll({ children }: { children: React.ReactNode }) {
  return <div className="-mx-1 overflow-x-auto px-1">{children}</div>;
}

// --- «Samlet» — selskap → adresse → lån, med delsummer -----------------------

function SamletTab({ status }: { status: LoanStatusResult }) {
  return (
    <div className="space-y-8">
      {status.companies.map((company) => (
        <section key={company.loanerId}>
          <div className="flex items-baseline gap-2 border-b border-white/10 px-1 pb-1.5">
            <h2 className="min-w-0 flex-1 truncate text-sm font-semibold text-white" title={company.companyName}>
              {company.companyName}
            </h2>
            <span className="shrink-0 text-sm font-semibold text-white">{formatNok(company.outstanding)}</span>
          </div>
          <div className="mt-2 space-y-3">
            {company.groups.map((g) => (
              <div key={g.address}>
                <div className="flex items-baseline gap-2 px-1">
                  <h3 className="min-w-0 flex-1 truncate text-[11px] font-medium text-white/70" title={g.address}>
                    {g.address}
                  </h3>
                  <span className="shrink-0 text-[11px] font-medium text-white/70">{formatNok(g.outstanding)}</span>
                </div>
                <TableScroll>
                  <table className="mt-1 w-full border-collapse">
                    <tbody className="divide-y divide-white/10">
                      {g.loans.map((l) => (
                        <tr key={l.loanId}>
                          <td className={cn(TD, "pl-1 text-white/45")}>#{l.loanId}</td>
                          <td className={TD}>
                            <RateCell l={l} />
                          </td>
                          <td className={cn(TD, "pr-1 text-right font-medium")}>{formatNok(l.outstanding)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableScroll>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

// --- «Alle lån» ---------------------------------------------------------------
// Mobil: Lån · Selskap · Rente · Utestående. Adresse + Hovedstol vises fra sm+.

function AlleLaanTab({ loans }: { loans: LoanStatusLine[] }) {
  return (
    <TableScroll>
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-white/10">
            <th className={TH}>Lån</th>
            <th className={TH}>Selskap</th>
            <th className={cn(TH, HIDE_MOBILE)}>Adresse</th>
            <th className={cn(TH, HIDE_MOBILE, "text-right")}>Hovedstol</th>
            <th className={TH}>Rente</th>
            <th className={cn(TH, "pr-1 text-right")}>Utestående</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {loans.map((l) => (
            <tr key={l.loanId}>
              <td className={cn(TD, "pl-1 text-white/45")}>#{l.loanId}</td>
              <td className={cn(TD, TRUNC)} title={l.companyName}>{l.companyName}</td>
              <td className={cn(TD, HIDE_MOBILE, TRUNC, "text-white/70")} title={l.address}>{l.address}</td>
              <td className={cn(TD, HIDE_MOBILE, "text-right")}>{formatNok(l.principal)}</td>
              <td className={TD}>
                <RateCell l={l} />
              </td>
              <td className={cn(TD, "pr-1 text-right font-medium")}>{formatNok(l.outstanding)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
  );
}

// --- «Renter» -------------------------------------------------------------
// Mobil: Lån · Selskap · Rente · Påløpt rente.

function RenterTab({ loans }: { loans: LoanStatusLine[] }) {
  return (
    <TableScroll>
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-white/10">
            <th className={TH}>Lån</th>
            <th className={TH}>Selskap</th>
            <th className={TH}>Rente</th>
            <th className={cn(TH, "pr-1 text-right")}>Påløpt rente</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {loans.map((l) => (
            <tr key={l.loanId}>
              <td className={cn(TD, "pl-1 text-white/45")}>#{l.loanId}</td>
              <td className={cn(TD, TRUNC)} title={l.companyName}>{l.companyName}</td>
              <td className={TD}>
                <RateCell l={l} />
              </td>
              <td className={cn(TD, "pr-1 text-right font-medium")}>{formatNok(l.accruedInterest)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
  );
}

// --- «Forlengelse» ----------------------------------------------------------
// Mobil: Lån · Selskap · Ny rente · Ny frist. Opprinnelig rente/forfall vises fra sm+.

function ForlengelseTab({ loans }: { loans: LoanStatusLine[] }) {
  const extended = loans.filter((l) => l.extended);
  if (extended.length === 0) return <div className="px-1 text-[11px] text-white/40">Ingen forlengede lån.</div>;
  return (
    <TableScroll>
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-white/10">
            <th className={TH}>Lån</th>
            <th className={TH}>Selskap</th>
            <th className={cn(TH, HIDE_MOBILE)}>Opprinnelig rente</th>
            <th className={TH}>Ny rente</th>
            <th className={cn(TH, HIDE_MOBILE)}>Opprinnelig forfall</th>
            <th className={cn(TH, "pr-1")}>Ny frist</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {extended.map((l) => (
            <tr key={l.loanId}>
              <td className={cn(TD, "pl-1 text-white/45")}>#{l.loanId}</td>
              <td className={cn(TD, TRUNC)} title={l.companyName}>{l.companyName}</td>
              <td className={cn(TD, HIDE_MOBILE)}>{l.rate} %</td>
              <td className={TD}><ExtBadge rate={l.extensionRate} /></td>
              <td className={cn(TD, HIDE_MOBILE, "text-white/70")}>{formatDate(l.dateExpire)}</td>
              <td className={cn(TD, "pr-1 text-white/70")}>{formatDate(l.extensionDue)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
  );
}

// --- «Pant» ------------------------------------------------------------------
// Mobil: Lån · Selskap · Sikkerhet. Adresse/matrikkel/kommune vises fra sm+.

function PantTab({ loans }: { loans: LoanStatusLine[] }) {
  return (
    <TableScroll>
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-white/10">
            <th className={TH}>Lån</th>
            <th className={TH}>Selskap</th>
            <th className={cn(TH, HIDE_MOBILE)}>Adresse</th>
            <th className={cn(TH, HIDE_MOBILE)}>Matrikkel</th>
            <th className={cn(TH, HIDE_MOBILE)}>Kommune</th>
            <th className={cn(TH, "pr-1")}>Sikkerhet</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {loans.map((l) => (
            <tr key={l.loanId}>
              <td className={cn(TD, "pl-1 text-white/45")}>#{l.loanId}</td>
              <td className={cn(TD, TRUNC)} title={l.companyName}>{l.companyName}</td>
              <td className={cn(TD, HIDE_MOBILE, TRUNC, "text-white/70")} title={l.address}>{l.address}</td>
              <td className={cn(TD, HIDE_MOBILE, "text-white/70")}>{l.matrikkel ?? "–"}</td>
              <td className={cn(TD, HIDE_MOBILE, "text-white/70")}>{l.kommune ?? "–"}</td>
              <td className={cn(TD, "pr-1")}>
                {l.security.length === 0 ? (
                  <span className="text-white/25">–</span>
                ) : (
                  l.security.map((s, i) => (
                    <div key={i} className="max-w-[140px] truncate text-white/70 sm:max-w-none" title={`${s.ownerName ?? s.owner ?? "Ukjent"}`}>
                      {s.ownerName ?? s.owner ?? "Ukjent"}
                      {s.amount != null ? ` · ${formatNok(s.amount)}` : ""}
                    </div>
                  ))
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
  );
}

// --- «Betalt» ------------------------------------------------------------------
// Én betalingshistorikk-tabell per lån som har delbetalinger — samme kolonner som
// kontor-vue sin PaymentHistory.vue (dato, dager, rentesats, forfalt/betalt rente,
// hovedstol betalt, gjenværende hovedstol, utestående renter), med sumrad nederst.
// Mobil: kun Dato · Betaling · Hovedstol betalt · Gjenværende hovedstol — resten fra sm+.

function PaymentHistoryTable({ l }: { l: LoanStatusLine }) {
  const totals = l.payments.reduce(
    (acc, p) => ({
      payment: acc.payment + p.paymentAmount,
      days: acc.days + p.days,
      interestDue: acc.interestDue + p.interestDue,
      interestPaid: acc.interestPaid + p.interestPaid,
      principalPaid: acc.principalPaid + p.principalPaid,
    }),
    { payment: 0, days: 0, interestDue: 0, interestPaid: 0, principalPaid: 0 }
  );

  return (
    <section>
      <div className="flex items-baseline gap-2 px-1">
        <h3 className="min-w-0 flex-1 truncate text-[11px] font-medium text-white/70">
          #{l.loanId} · {l.companyName}
        </h3>
        <span className="shrink-0 text-[10px] text-white/40">{l.payments.length} betaling{l.payments.length === 1 ? "" : "er"}</span>
      </div>
      <TableScroll>
        <table className="mt-1 w-full border-collapse">
          <thead>
            <tr className="border-b border-white/10">
              <th className={TH}>Dato</th>
              <th className={cn(TH, "text-right")}>Betaling</th>
              <th className={cn(TH, HIDE_MOBILE, "text-right")}>Dager</th>
              <th className={cn(TH, HIDE_MOBILE, "text-right")}>Rentesats</th>
              <th className={cn(TH, HIDE_MOBILE, "text-right")}>Forfalt rente</th>
              <th className={cn(TH, HIDE_MOBILE, "text-right")}>Rente betalt</th>
              <th className={cn(TH, "text-right")}>Hovedstol betalt</th>
              <th className={cn(TH, "text-right")}>Gjenv. hovedstol</th>
              <th className={cn(TH, HIDE_MOBILE, "pr-1 text-right")}>Utestående renter</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {l.payments.map((p) => (
              <tr key={p.paymentNumber}>
                <td className={cn(TD, "pl-1 text-white/70")}>{formatDate(p.paymentDate)}</td>
                <td className={cn(TD, "text-right")}>{formatNok(p.paymentAmount)}</td>
                <td className={cn(TD, HIDE_MOBILE, "text-right text-white/45")}>{p.days}</td>
                <td className={cn(TD, HIDE_MOBILE, "text-right")}>
                  {Math.round(p.ratePercent)} %{p.isAfterExpiry && <span className="text-white/40"> (forl.)</span>}
                </td>
                <td className={cn(TD, HIDE_MOBILE, "text-right")}>{formatNok(p.interestDue)}</td>
                <td className={cn(TD, HIDE_MOBILE, "text-right")}>{formatNok(p.interestPaid)}</td>
                <td className={cn(TD, "text-right")}>{p.principalPaid > 0 ? formatNok(p.principalPaid) : "–"}</td>
                <td className={cn(TD, "text-right font-medium")}>{formatNok(p.principalRemaining)}</td>
                <td className={cn(TD, HIDE_MOBILE, "pr-1 text-right", p.unpaidInterestRemaining > 0 && "text-amber-300")}>
                  {p.unpaidInterestRemaining > 0 ? formatNok(p.unpaidInterestRemaining) : "–"}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-white/10 bg-white/[0.03]">
              <td className={cn(TD, "pl-1 font-medium")}>Totalt</td>
              <td className={cn(TD, "text-right font-medium")}>{formatNok(totals.payment)}</td>
              <td className={cn(TD, HIDE_MOBILE, "text-right text-white/45")}>{totals.days}</td>
              <td className={cn(TD, HIDE_MOBILE)}></td>
              <td className={cn(TD, HIDE_MOBILE, "text-right font-medium")}>{formatNok(totals.interestDue)}</td>
              <td className={cn(TD, HIDE_MOBILE, "text-right font-medium")}>{formatNok(totals.interestPaid)}</td>
              <td className={cn(TD, "text-right font-medium")}>{formatNok(totals.principalPaid)}</td>
              <td className={TD}></td>
              <td className={cn(TD, HIDE_MOBILE, "pr-1")}></td>
            </tr>
          </tfoot>
        </table>
      </TableScroll>
    </section>
  );
}

function BetaltTab({ loans }: { loans: LoanStatusLine[] }) {
  const paid = loans.filter((l) => l.payments.length > 0);
  if (paid.length === 0) return <div className="px-1 text-[11px] text-white/40">Ingen registrerte delbetalinger ennå.</div>;
  return (
    <div className="space-y-6">
      {paid.map((l) => (
        <PaymentHistoryTable key={l.loanId} l={l} />
      ))}
    </div>
  );
}

// --- «Utbetaling» --------------------------------------------------------------
// Mobil: Lån · Selskap · Hovedstol · Utbetalt dato. Adresse vises fra sm+.

function UtbetalingTab({ loans }: { loans: LoanStatusLine[] }) {
  return (
    <TableScroll>
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-white/10">
            <th className={TH}>Lån</th>
            <th className={TH}>Selskap</th>
            <th className={cn(TH, HIDE_MOBILE)}>Adresse</th>
            <th className={TH}>Hovedstol</th>
            <th className={cn(TH, "pr-1")}>Utbetalt dato</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {loans.map((l) => (
            <tr key={l.loanId}>
              <td className={cn(TD, "pl-1 text-white/45")}>#{l.loanId}</td>
              <td className={cn(TD, TRUNC)} title={l.companyName}>{l.companyName}</td>
              <td className={cn(TD, HIDE_MOBILE, TRUNC, "text-white/70")} title={l.address}>{l.address}</td>
              <td className={TD}>{formatNok(l.principal)}</td>
              <td className={cn(TD, "pr-1 text-white/70")}>{formatDate(l.datePayout)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
  );
}

/** Låntakers lånestatus — totalt utestående/hovedstol/renter + detaljerte underfaner
 *  (alle lån, renter, forlengelse, pant, betalt, utbetaling). Loaner-only (route-gated);
 *  investors never see this tab. Routed at /laanestatus. Kun ÅPNE lån vises noe sted
 *  (avsluttede lån filtreres bort i backend, se loanStatus.ts). Tekst holdes ≤12px
 *  gjennomgående (10–11px i tabellene); sekundære kolonner skjules under sm-breakpointet
 *  i stedet for å brekke — se HIDE_MOBILE/TRUNC. */
export function LoanStatusPage() {
  const role = useAuthStore((s) => s.user?.role);
  const { status, isLoading, isError } = useLoanStatus();
  const [tab, setTab] = useState<TabId>("samlet");

  if (role !== "loaner") return <Navigate to="/dashboard" replace />;

  return (
    <div className="flex h-screen w-full overflow-hidden bg-[radial-gradient(120%_120%_at_50%_0%,#134e4a_0%,#0a2a2e_42%,#020617_100%)] text-white">
      <IconRail />

      <main className="flex-1 overflow-y-auto px-3 py-6 sm:px-6 sm:py-10">
        <div className="mx-auto w-full max-w-4xl">
          <h1 className="px-1 text-xl font-semibold sm:text-2xl">Lånestatus</h1>

          {isLoading ? (
            <div className="mt-6 px-1 text-[11px] text-white/40">Laster …</div>
          ) : isError || !status ? (
            <div className="mt-6 px-1 text-[11px] text-red-300">Kunne ikke laste lånestatus.</div>
          ) : status.allLoans.length === 0 ? (
            <div className="mt-6 px-1 text-[11px] text-white/40">Ingen åpne lån.</div>
          ) : (
            <>
              <div className="mt-4 grid grid-cols-3 overflow-hidden rounded-md border border-white/10 bg-white/[0.04]">
                <Stat label="Totalt utestående" value={formatNok(status.totalOutstanding)} hint="hovedstol + renter" />
                <Stat label="Hovedstol" value={formatNok(status.totalPrincipal)} />
                <Stat label="Påløpte renter" value={formatNok(status.totalAccruedInterest)} />
              </div>
              <div className="mt-2 px-1 text-[10px] text-white/35">
                {status.loanCount} åpne lån · {status.companyCount} selskap
                {status.companyCount === 1 ? "" : "er"} · pr. {status.asOf}
              </div>

              <div className="mt-5 flex flex-wrap gap-1 border-b border-white/10 px-1">
                {TABS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTab(t.id)}
                    className={cn(
                      "rounded-t-md px-2.5 py-1.5 text-[11px] font-medium transition-colors",
                      tab === t.id
                        ? "bg-teal-400/15 text-teal-300"
                        : "text-white/45 hover:text-white"
                    )}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <div className="mt-4">
                {tab === "samlet" && <SamletTab status={status} />}
                {tab === "alle" && <AlleLaanTab loans={status.allLoans} />}
                {tab === "renter" && <RenterTab loans={status.allLoans} />}
                {tab === "forlengelse" && <ForlengelseTab loans={status.allLoans} />}
                {tab === "pant" && <PantTab loans={status.allLoans} />}
                {tab === "betalt" && <BetaltTab loans={status.allLoans} />}
                {tab === "utbetaling" && <UtbetalingTab loans={status.allLoans} />}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
